import {
  Button,
  Textarea,
  tokens,
} from '@fluentui/react-components';
import {
  AddRegular,
  DeleteRegular,
  ChatRegular,
  StarRegular,
  StarFilled,
  ArrowSyncRegular,
} from '@fluentui/react-icons';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import type { TaxReturn } from '../../types';
import { statusDisplayNames } from '../../types';
import type { TaxReturnStatus } from '../../types';
import NotificationHistoryPanel from '../../components/NotificationHistoryPanel';
import type { UseMutationResult } from '@tanstack/react-query';

interface MergedNote {
  kind: 'manual' | 'status-change';
  id: string;
  content: string;
  createdAt: string;
  createdByName?: string;
  isImportant: boolean;
  fromStatus?: TaxReturnStatus | null;
  toStatus?: TaxReturnStatus;
}

export interface ActivityTabProps {
  taxReturn: TaxReturn;
  taxReturnId: string;
  newNoteContent: string;
  onNewNoteContentChange: (val: string) => void;
  newNoteImportant: boolean;
  onNewNoteImportantChange: (val: boolean) => void;
  addNoteMutation: UseMutationResult<unknown, Error, { content: string; isImportant: boolean }>;
  deleteNoteMutation: UseMutationResult<unknown, Error, string>;
  onAddNote: () => void;
}

export function ActivityTab({
  taxReturn,
  taxReturnId,
  newNoteContent,
  onNewNoteContentChange,
  newNoteImportant,
  onNewNoteImportantChange,
  addNoteMutation,
  deleteNoteMutation,
  onAddNote,
}: ActivityTabProps) {
  const { t } = useTranslation();

  const statusLabel = (status: string | null | undefined): string => {
    if (!status) return t('activityTab.notes.noneStatus');
    return statusDisplayNames[status as TaxReturnStatus] ?? status;
  };

  const mergedNotes = useMemo<MergedNote[]>(() => {
    const items: MergedNote[] = [];

    if (taxReturn.notes) {
      for (const note of taxReturn.notes) {
        items.push({
          kind: 'manual',
          id: note.id,
          content: note.content,
          createdAt: note.createdAt,
          createdByName: note.createdByName,
          isImportant: note.isImportant,
        });
      }
    }

    if (taxReturn.statusHistory) {
      for (const change of taxReturn.statusHistory) {
        if (change.notes && change.notes.trim()) {
          items.push({
            kind: 'status-change',
            id: `status-${change.changedAt}-${change.toStatus}`,
            content: change.notes,
            createdAt: change.changedAt,
            createdByName: change.changedByName,
            isImportant: false,
            fromStatus: change.fromStatus,
            toStatus: change.toStatus,
          });
        }
      }
    }

    items.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return items;
  }, [taxReturn.notes, taxReturn.statusHistory]);

  return (
    <>
      {/* Notes/Comments Section */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">
            <ChatRegular style={{ marginRight: '8px' }} />
            {t('activityTab.notes.heading', { count: mergedNotes.length })}
          </span>
        </div>
        <div style={{ padding: '16px' }}>
          {/* Add New Note Form */}
          <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: 'var(--tl-bg-subtle)', borderRadius: '8px' }}>
            <Textarea
              placeholder={t('activityTab.notes.placeholder')}
              value={newNoteContent}
              onChange={(_, data) => onNewNoteContentChange(data.value)}
              rows={3}
              style={{ width: '100%', marginBottom: '12px' }}
            />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={newNoteImportant}
                  onChange={(e) => onNewNoteImportantChange(e.target.checked)}
                />
                <StarRegular style={{ color: newNoteImportant ? '#f59e0b' : 'var(--tl-text-tertiary)' }} />
                <span style={{ fontSize: tokens.fontSizeBase300, color: 'var(--tl-text-secondary)' }}>{t('activityTab.notes.markImportant')}</span>
              </label>
              <Button
                appearance="primary"
                icon={<AddRegular />}
                disabled={!newNoteContent.trim() || addNoteMutation.isPending}
                onClick={onAddNote}
              >
                {addNoteMutation.isPending ? t('activityTab.notes.adding') : t('activityTab.notes.addNote')}
              </Button>
            </div>
          </div>

          {/* Merged Notes List */}
          {mergedNotes.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {mergedNotes.map((note) => (
                  <div
                    key={note.id}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: note.isImportant
                        ? '#fffbeb'
                        : note.kind === 'status-change'
                          ? '#f0f4ff'
                          : '#ffffff',
                      border: note.isImportant
                        ? '1px solid #f59e0b'
                        : note.kind === 'status-change'
                          ? '1px solid #c7d2fe'
                          : '1px solid var(--tl-border)',
                      borderRadius: '8px',
                      borderLeft: note.isImportant
                        ? '4px solid #f59e0b'
                        : note.kind === 'status-change'
                          ? '4px solid #6366f1'
                          : '4px solid var(--tl-border)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', flexWrap: 'wrap' }}>
                          {note.isImportant && (
                            <StarFilled style={{ color: '#f59e0b', fontSize: tokens.fontSizeBase300 }} />
                          )}
                          {note.kind === 'status-change' && (
                            <ArrowSyncRegular style={{ color: '#6366f1', fontSize: tokens.fontSizeBase300 }} />
                          )}
                          <span style={{ fontWeight: 500, fontSize: '13px', color: 'var(--tl-text-primary)' }}>
                            {note.createdByName || t('activityTab.notes.unknownAuthor')}
                          </span>
                          <span style={{ fontSize: tokens.fontSizeBase200, color: 'var(--tl-text-tertiary)' }}>
                            {format(new Date(note.createdAt), 'MMM d, yyyy h:mm a')}
                          </span>
                        </div>
                        {note.kind === 'status-change' && (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            marginBottom: '8px',
                            padding: '4px 8px',
                            backgroundColor: '#e0e7ff',
                            borderRadius: '4px',
                            fontSize: tokens.fontSizeBase200,
                            color: '#4338ca',
                            fontWeight: 500,
                          }}>
                            {t('activityTab.notes.statusChanged', {
                              from: statusLabel(note.fromStatus),
                              to: statusLabel(note.toStatus),
                            })}
                          </div>
                        )}
                        <p style={{ margin: 0, fontSize: tokens.fontSizeBase300, color: 'var(--tl-text-primary)', whiteSpace: 'pre-wrap' }}>
                          {note.content}
                        </p>
                      </div>
                      {note.kind === 'manual' && (
                        <Button
                          appearance="subtle"
                          icon={<DeleteRegular />}
                          size="small"
                          onClick={() => deleteNoteMutation.mutate(note.id)}
                          disabled={deleteNoteMutation.isPending}
                          title={t('activityTab.notes.deleteNoteTooltip')}
                        />
                      )}
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <div className="empty-state" style={{ textAlign: 'center', padding: '24px', color: 'var(--tl-text-tertiary)' }}>
              {t('activityTab.notes.emptyState')}
            </div>
          )}
        </div>
      </div>

      {/* Notification History */}
      <div className="card" style={{ marginTop: '24px' }}>
        <div style={{ padding: '16px' }}>
          <NotificationHistoryPanel
            taxReturnId={taxReturnId}
            clientName={taxReturn.clientName}
            clientEmail={taxReturn.clientEmail}
            clientPhone={taxReturn.clientPhone}
            preparerName={taxReturn.preparerName}
            preparerEmail={taxReturn.preparerEmail}
            reviewerName={taxReturn.reviewerName}
            reviewerEmail={taxReturn.reviewerEmail}
          />
        </div>
      </div>
    </>
  );
}
